// components/create/PassesSection.js
import React, { useState } from 'react';
import '../../styles/PassesSection.scss';

const PASS_TYPES = [
  { id: 'free', label: 'Free Event', description: 'No cost to attend' },
  { id: 'paid', label: 'Paid Event', description: 'Charge for tickets/passes' },
  { id: 'donation', label: 'Donation', description: 'Suggested donation amount' },
];

export default function PassesSection({
  passes,
  setPasses,
  globalPassType,
  setGlobalPassType,
  isGstRegistered,
  setIsGstRegistered,
  gstNumber,
  setGstNumber,
  panNumber,
  setPanNumber,
  bankDetails,
  setBankDetails,
  contactPerson,
  setContactPerson,
  isEditMode,
  hasSales,
  originalBankAccountId,
}) {
  const [showPassModal, setShowPassModal] = useState(false);
  const [currentPass, setCurrentPass] = useState(null);

  const handleAddPass = () => {
    setCurrentPass(null);
    setShowPassModal(true);
  };

  const handleEditPass = (pass) => {
    setCurrentPass(pass);
    setShowPassModal(true);
  };

  const handleSavePass = (pass) => {
    const index = passes.findIndex((p) => p.id === pass.id);
    if (index !== -1) {
      const newPasses = [...passes];
      newPasses[index] = pass;
      setPasses(newPasses);
    } else {
      setPasses([...passes, pass]);
    }
    setShowPassModal(false);
  };

  const handleDeletePass = (id) => {
    setPasses(passes.filter((pass) => pass.id !== id));
  };

  const isPaidEvent = globalPassType.id === 'paid' || globalPassType.id === 'donation';

  return (
    <div className="passes-section">
      {/* Pass Type */}
      <div className="section-card">
        <label className="section-title">Event Type</label>
        <div className="option-group">
          {PASS_TYPES.map((type) => (
            <button
              key={type.id}
              type="button"
              className={`option-button ${globalPassType.id === type.id ? 'active' : ''}`}
              onClick={() => setGlobalPassType(type)}
            >
              <div className="option-content">
                <span className="option-label">{type.label}</span>
                <span className="option-description">{type.description}</span>
              </div>
              {globalPassType.id === type.id && (
                <i className="icon-checkmark-circle"></i>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Passes List - Only show for paid events */}
      {isPaidEvent && (
        <div className="section-card">
          <label className="section-title">Ticket/Pass Types</label>
          <p className="section-description">
            Create different ticket types with varying prices
          </p>

          {passes.map((pass) => (
            <div key={pass.id} className="pass-item">
              <div className="pass-content">
                <div className="pass-text">
                  <p className="pass-name">{pass.name}</p>
                  <p className="pass-details">
                    {pass.price > 0 ? `₹${pass.price}` : 'Free'} • 
                    {pass.quantity ? ` ${pass.quantity} available` : ' Unlimited'}
                  </p>
                </div>
              </div>
              <div className="pass-actions">
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => handleEditPass(pass)}
                >
                  <i className="icon-create"></i>
                </button>
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => handleDeletePass(pass.id)}
                >
                  <i className="icon-trash"></i>
                </button>
              </div>
            </div>
          ))}

          <button
            type="button"
            className="add-button"
            onClick={handleAddPass}
          >
            <i className="icon-add-circle"></i>
            <span>Add Pass Type</span>
          </button>
        </div>
      )}

      {/* GST Registration - Only show for paid events */}
      {isPaidEvent && (
        <div className="section-card">
          <div className="toggle-row">
            <div className="toggle-info">
              <p className="toggle-label">GST Registered</p>
              <p className="toggle-description">Are you registered for GST?</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={isGstRegistered}
                onChange={(e) => setIsGstRegistered(e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          {isGstRegistered && (
            <>
              <div className="input-group">
                <label className="field-label">GST Number</label>
                <input
                  type="text"
                  className="text-input"
                  placeholder="Enter GST number"
                  value={gstNumber}
                  onChange={(e) => setGstNumber(e.target.value)}
                />
              </div>

              <div className="input-group">
                <label className="field-label">PAN Number</label>
                <input
                  type="text"
                  className="text-input"
                  placeholder="Enter PAN number"
                  value={panNumber}
                  onChange={(e) => setPanNumber(e.target.value)}
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* Bank Details - Only show for paid events */}
      {isPaidEvent && (
        <div className="section-card">
          <label className="section-title">Bank Details</label>
          <p className="section-description">
            {isEditMode && hasSales && originalBankAccountId
              ? "⚠️ Bank details cannot be changed after sales have been made"
              : "Required for receiving payments"}
          </p>

          <div className="input-group">
            <label className="field-label">Account Holder Name</label>
            <input
              type="text"
              className="text-input"
              placeholder="Enter account holder name"
              value={bankDetails.accountHolderName || ''}
              onChange={(e) => setBankDetails({ ...bankDetails, accountHolderName: e.target.value })}
              disabled={isEditMode && hasSales && originalBankAccountId}
            />
          </div>

          <div className="input-group">
            <label className="field-label">Account Number</label>
            <input
              type="text"
              className="text-input"
              placeholder="Enter account number"
              value={bankDetails.accountNumber || ''}
              onChange={(e) => setBankDetails({ ...bankDetails, accountNumber: e.target.value })}
              disabled={isEditMode && hasSales && originalBankAccountId}
            />
          </div>

          <div className="input-group">
            <label className="field-label">IFSC Code</label>
            <input
              type="text"
              className="text-input"
              placeholder="Enter IFSC code"
              value={bankDetails.ifscCode || ''}
              onChange={(e) => setBankDetails({ ...bankDetails, ifscCode: e.target.value })}
              disabled={isEditMode && hasSales && originalBankAccountId}
            />
          </div>

          <div className="input-group">
            <label className="field-label">Bank Name</label>
            <input
              type="text"
              className="text-input"
              placeholder="Enter bank name"
              value={bankDetails.bankName || ''}
              onChange={(e) => setBankDetails({ ...bankDetails, bankName: e.target.value })}
              disabled={isEditMode && hasSales && originalBankAccountId}
            />
          </div>
        </div>
      )}

      {/* Contact Person - Only show for paid events */}
      {isPaidEvent && (
        <div className="section-card">
          <label className="section-title">Contact Person</label>
          <p className="section-description">
            Person responsible for the event
          </p>

          <div className="input-group">
            <label className="field-label">Name</label>
            <input
              type="text"
              className="text-input"
              placeholder="Enter name"
              value={contactPerson.name || ''}
              onChange={(e) => setContactPerson({ ...contactPerson, name: e.target.value })}
            />
          </div>

          <div className="input-group">
            <label className="field-label">Email</label>
            <input
              type="email"
              className="text-input"
              placeholder="Enter email"
              value={contactPerson.email || ''}
              onChange={(e) => setContactPerson({ ...contactPerson, email: e.target.value })}
            />
          </div>

          <div className="input-group">
            <label className="field-label">Phone</label>
            <input
              type="tel"
              className="text-input"
              placeholder="Enter phone number"
              value={contactPerson.phone || ''}
              onChange={(e) => setContactPerson({ ...contactPerson, phone: e.target.value })}
            />
          </div>
        </div>
      )}
    </div>
  );
}